import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { Card, Button, AnimatePresence } from '../teacher/components/shared/UI';
import EntryFormFields, { FormRow, inputStyle, getRecommendedLevelIfBelow, getNonAttendingChoreographers } from '../shared/entryForm/EntryFormFields';
import LevelWarningModal from '../shared/entryForm/LevelWarningModal';
import usePageTitle from '../shared/hooks/usePageTitle';

const ALLOWED_TYPES = ['Standard', 'Creative Collaboration'];

const EMPTY_FORM = {
  id: null, title: '', type: 'Standard', style: 'Ballet', size: 'Solo', level: 'Novice',
  dancerIds: [], choreographerIds: [], props: false, propDescription: '', startPosition: '', acknowledgement: false,
};

// Public, no-login page a choreographer reaches via a school-scoped link a
// teacher generates on the Entry Library page (see ChoreographerLinkPanel).
// Lets them submit an entry directly from their own roster — it lands as
// Pending Review for the teacher to approve or edit. Reuses the same
// EntryFormFields the teacher's own "+ New entry" form uses (freestyle
// handling isn't relevant here since Freestyle isn't offered, but the K-Pop
// credit field, size-mismatch check, and recommended-level nudge all are).
export default function ChoreographerSubmission() {
  usePageTitle('OSSDF Choreographer Entry');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  const [dancers, setDancers] = useState([]);
  const [groups, setGroups] = useState([]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submittedByName, setSubmittedByName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [levelWarning, setLevelWarning] = useState(null); // null | recommended level name

  useEffect(() => {
    if (!token) { setLoadError('This link is missing a token — ask your teacher for the full link.'); setLoading(false); return; }
    fetch(`${API_BASE}/api/register/choreographer-info?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'This link is invalid or has been revoked. Ask your teacher for a new one.');
        }
        return res.json();
      })
      .then((data) => {
        setSchoolName(data.schoolName);
        setDancers(data.dancers || []);
        setGroups(data.groups || []);
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const isCreativeCollab = form.type === 'Creative Collaboration';
  const nonAttendingChoreographers = getNonAttendingChoreographers(form);
  const canSubmit = submittedByName.trim() && form.acknowledgement && form.title && (form.choreographerIds.length || nonAttendingChoreographers) && form.dancerIds.length &&
    form.startPosition && (form.style !== 'K-Pop' || form.originalChoreographers?.trim());

  // Same below-recommended-level block the teacher's EntryEditor uses.
  const handleSubmitClick = () => {
    const recommendedLevel = getRecommendedLevelIfBelow(form, dancers);
    if (recommendedLevel) {
      setLevelWarning(recommendedLevel);
      return;
    }
    handleSubmit();
  };

  const handleSubmit = async () => {
    setLevelWarning(null);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/api/register/choreographer-entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          submittedByName: submittedByName.trim(),
          title: form.title,
          level: form.level,
          style: isCreativeCollab ? 'Creative Collaboration' : form.style,
          size: form.size,
          numOfDancers: form.dancerIds.length,
          choreographerIds: form.choreographerIds,
          nonAttendingChoreographers,
          dancerIds: form.dancerIds,
          originalChoreographers: form.style === 'K-Pop' ? (form.originalChoreographers || '') : '',
          acknowledgement: true,
          props: form.propDescription || '',
          startPosition: form.startPosition,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Submission failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: 20, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Submit a dance entry</h1>
      {loading ? (
        <Card><span style={{ fontSize: 13, color: '#666' }}>Loading…</span></Card>
      ) : loadError ? (
        <Card><span style={{ fontSize: 13, color: '#C0392B' }}>{loadError}</span></Card>
      ) : submitted ? (
        <Card title="Submitted">
          <span style={{ fontSize: 14 }}>
            Thanks — your entry has been submitted for {schoolName}'s teacher to review. They'll follow up if they have questions.
          </span>
        </Card>
      ) : (
        <Card title={schoolName} subtitle="Fill out the entry below. Your teacher will review it before it's finalized — they can edit anything if needed.">
          <FormRow label="Your name" required>
            <span style={{ fontSize: 13, fontStyle: 'italic', color: '#666' }}>So your teacher knows who submitted this.</span>
            <input style={inputStyle} value={submittedByName} onChange={(e) => setSubmittedByName(e.target.value)} placeholder="Your full name" />
          </FormRow>

          <EntryFormFields form={form} setForm={setForm} dancers={dancers} groups={groups} allowedTypes={ALLOWED_TYPES} />

          {submitError && <div style={{ fontSize: 13, color: '#C0392B', marginBottom: 10 }}>{submitError}</div>}

          <Button variant="primary" onClick={handleSubmitClick} disabled={!canSubmit || submitting}>
            {submitting ? 'Submitting…' : '✓ Submit entry'}
          </Button>

          <AnimatePresence>
            {levelWarning && (
              <LevelWarningModal
                key="level-warning-modal"
                level={form.level}
                recommendedLevel={levelWarning}
                onClose={() => setLevelWarning(null)}
              />
            )}
          </AnimatePresence>
        </Card>
      )}
    </div>
  );
}
